import React, { useState, useEffect, useRef } from 'react';
import { Users, MapPin, ThumbsUp, Award } from 'lucide-react';
import { stats } from '../mockData';

const iconComponents = {
  Users,
  MapPin,
  ThumbsUp,
  Award
};

const AnimatedCounter = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const endNum = parseInt(end.replace(/[^0-9]/g, ''));
          const increment = endNum / (duration / 16);
          let current = 0;

          const timer = setInterval(() => {
            current += increment;
            if (current >= endNum) {
              setCount(endNum);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, 16);

          return () => clearInterval(timer);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [end, duration, hasAnimated]);

  const suffix = end.includes('+') ? '+' : '';
  const displayValue = count.toString() + suffix;

  return <span ref={ref}>{hasAnimated ? displayValue : '0'}</span>;
};

const Stats = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-[#2E3C5B] via-[#1f2a3f] to-[#2E3C5B] text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#E94B3C] rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#E94B3C] rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold">
            Why Choose EBR Systems?
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Trusted by thousands across India for quality service and genuine parts
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => {
            const IconComponent = iconComponents[stat.icon];
            return (
              <div
                key={stat.id}
                className="text-center group cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/20 hover:border-[#E94B3C] transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                  {/* Icon */}
                  <div className="w-16 h-16 bg-[#E94B3C] rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:rotate-6 group-hover:scale-110 transition-all duration-300 shadow-lg">
                    <IconComponent size={32} className="text-white" />
                  </div>

                  {/* Number */}
                  <div className="text-5xl font-bold mb-2 text-white group-hover:text-[#E94B3C] transition-colors">
                    <AnimatedCounter end={stat.number} />
                  </div>

                  {/* Label */}
                  <p className="text-lg text-gray-200 font-medium">
                    {stat.label}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Text */}
        <div className="mt-16 text-center">
          <p className="text-xl text-gray-200">
            Serving India since <span className="text-[#E94B3C] font-bold">2007</span> with excellence and integrity
          </p>
        </div>
      </div>
    </section>
  );
};

export default Stats;
